<template>
  <div class="container pb-4">
    <o-bread-crumb>
      <template v-slot:pageName>Corporate</template>
      <template v-slot:pageDetail>Transaction Report Detail</template>
    </o-bread-crumb>

    <!-- Action Bar -->
    <div class="detail-action-bar">
      <button @click="goBack" class="btn-back">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <div v-if="reportData" class="report-date-badge">
        <i class="fas fa-calendar-alt me-2"></i> {{ reportData.formatted_date }}
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="alert alert-danger mt-2" role="alert">
      {{ error }}
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="detail-loading-state">
      <div class="d-flex justify-content-center spinner-border primary-icon"></div>
      <p class="mt-3 text-muted">Loading report detail...</p>
    </div>

    <div v-if="!loading" class="card bg-white shadow-sm mt-3 border-0">
      <!-- Card Header -->
      <div class="card-header bg-white border-bottom-0 pt-4 pb-2">
        <div class="d-flex justify-content-between align-items-center px-2">
          <div>
            <h4 class="header-title mb-1">
              <i class="fas fa-info-circle me-2" style="color: var(--primary-color);"></i>
              Transaction Detail
              <span
                v-if="reportData && reportData.report_type !== 'normal'"
                class="badge ms-2"
                :class="reportData.report_type === 'corporate_manipulation' ? 'bg-warning text-dark' : 'bg-info text-white'"
                style="font-size: 0.8rem; vertical-align: middle;"
              >
                {{ reportTypeLabel }}
              </span>
            </h4>
            <p class="header-subtitle text-muted mb-0" v-if="reportData">
              {{ reportData.branch_name && reportData.merchant_name
                ? reportData.branch_name + ' • ' + reportData.merchant_name
                : reportData.corporate_name || '-' }}
            </p>
          </div>
        </div>
      </div>

      <div class="card-body px-4">
        <!-- Empty State -->
        <div v-if="!reportData" class="text-center py-5 text-muted">
          <p>No report data available</p>
        </div>

        <div v-else class="report-detail-content">

          <!-- Grand Summary Cards -->
          <div class="row mb-4 summary-row">
            <div class="col-md-4">
              <div class="summary-card">
                <span class="summary-label">Members</span>
                <span class="summary-value">{{ reportData.summary?.total_member_count || 0 }}</span>
              </div>
            </div>
            <div class="col-md-4" v-if="isPointSystemActive">
              <div class="summary-card card-positive">
                <span class="summary-label">
                  Total Earn 
                  <span class="count-badge earn-badge">{{ reportData.summary?.total_earn_count || 0 }}</span>
                </span>
                <span class="summary-value text-success">+{{ formatNumber(reportData.summary?.total_earn_points || 0) }}</span>
              </div>
            </div>
            <div class="col-md-4" v-if="isPointSystemActive">
              <div class="summary-card card-negative">
                <span class="summary-label">
                  Total Redeem
                  <span class="count-badge redeem-badge">{{ reportData.summary?.total_redeem_count || 0 }}</span>
                </span>
                <span class="summary-value text-danger">-{{ formatNumber(reportData.summary?.total_redeem_points || 0) }}</span>
              </div>
            </div>
          </div>

          <!-- Status & Members Summary Row -->
          <div class="row mb-4 status-summary-row">
            <div :class="isPointSystemActive ? 'col-md-4' : 'col-md-12'">
              <div class="summary-card">
                <span class="summary-label">Total Transactions</span>
                <span class="summary-value">{{ formatNumber(reportData.sum_transaction) }}</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="summary-card card-status-active">
                <span class="summary-label">
                  <i class="fas fa-check-circle me-1" style="color: #155724;"></i>
                  Active Amount (IDR)
                </span>
                <span class="summary-value">{{ formatNumber(activeTransactionAmount) }}</span>
                <span class="status-count-note">{{ activeTransactionCount }} transaction(s)</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="summary-card card-status-cancelled">
                <span class="summary-label">
                  <i class="fas fa-times-circle me-1" style="color: #991b1b;"></i>
                  Cancelled Amount (IDR)
                </span>
                <span class="summary-value text-danger">{{ formatNumber(cancelledTransactionAmount) }}</span>
                <span class="status-count-note">{{ cancelledTransactionCount }} transaction(s)</span>
              </div>
            </div>
          </div>


          <!-- Transactions Table -->
          <o-table style="text-align:left;" class="table-hover transaction-list-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th class="col-date">Time</th>
                <th class="col-code">Transaction Code</th>
                <th class="col-member">Member Email</th>
                <th class="col-info">Source</th>
                <th class="col-product">Voucher / Description</th>
                <th class="col-total text-end">Amount (IDR)</th>
                <th class="col-point text-end" v-if="isPointSystemActive">Point In</th>
                <th class="col-point text-end" v-if="isPointSystemActive">Point Out</th>
                <th class="col-status text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!reportData.transactions || reportData.transactions.length === 0">
                <td :colspan="isPointSystemActive ? 10 : 8" class="text-center py-4 text-muted">
                  No transactions found
                </td>
              </tr>
              <tr v-for="(trx, idx) in reportData.transactions" :key="idx">
                <td>{{ idx + 1 }}</td>
                <td>
                  <span class="date-text text-nowrap">{{ trx.time }}</span>
                </td>
                <td><span class="text-muted small">{{ trx.transaction_code || '-' }}</span></td>
                <td>
                  <span class="fw-medium">{{ trx.member_email || '-' }}</span>
                </td>
                <td>
                  <span class="source-badge" :class="getSourceClass(trx)">
                    {{ trx.source }}
                  </span>
                </td>
                <td>{{ trx.voucher_name || '-' }}</td>
                <td class="text-end fw-bold">{{ formatNumber(trx.amount) }}</td>
                <td class="text-end text-theme fw-bold" v-if="isPointSystemActive">
                  <div v-if="trx.points_in" class="d-flex align-items-center gap-1 justify-content-end">
                    <i class="fas fa-coins text-theme"></i>
                    <span class="point-value fw-bold text-theme">+{{ formatNumber(trx.points_in) }}</span>
                  </div>
                  <span v-else>-</span>
                </td>
                <td class="text-end text-danger fw-bold" v-if="isPointSystemActive">
                  <div v-if="trx.points_out" class="d-flex align-items-center gap-1 justify-content-end">
                    <i class="fas fa-coins text-danger"></i>
                    <span class="point-value fw-bold text-danger">-{{ formatNumber(trx.points_out) }}</span>
                  </div>
                  <span v-else>-</span>
                </td>
                <td class="text-center">
                  <div class="status-badge-wrapper">
                    <span class="trx-status-badge" :class="getStatusClass(trx)">
                      {{ getStatusLabel(trx) }}
                    </span>
                    <div v-if="getStatusTooltip(trx) && isCancelled(trx)" class="custom-tooltip">
                      {{ getStatusTooltip(trx) }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </o-table>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { OBreadCrumb, OTable } from '@/components/OmnihControl';
import { usePointSystem } from '@/composables/pointsystem';
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from '@/composables/useToast';

export default {
  name: 'TransactionReportDetail',
  components: {
    OBreadCrumb,
    OTable,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { isPointSystemActive } = usePointSystem();
    const { showToast } = useToast();

    const reportId = route.params.id;
    const loading = ref(true);
    const error = ref(null);
    const reportData = ref(null);

    const reportTypeLabel = computed(() => {
      if (!reportData.value) return '';
      const map = {
        'corporate_manipulation': 'Point Manipulation Corporate',
        'merchant_manipulation': 'Point Manipulation Merchant',
      };
      return map[reportData.value.report_type] || '';
    });

    const formatNumber = (value) => {
      if (value === null || value === undefined) return '0';
      return Number(value).toLocaleString();
    };

    const goBack = () => {
      router.push({ name: 'TransactionReport' });
    };

    const fetchDetail = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await store.dispatch('TransactionReport/readById', reportId);
        reportData.value = response?.data?.data || null;
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to load report detail.';
        showToast({
          type: 'error',
          title: 'Load Failed',
          message: error.value,
        });
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchDetail();
    });

    const getSourceClass = (trx) => {
      if (!trx) return 'internal';
      if (trx.source === 'External (Nett)') return 'external-nett';
      if (trx.source === 'External (Gross)') return 'external-gross';
      if (trx.type === 'Redeem') return 'external-gross';
      return 'internal';
    };

    const getStatusClass = (trx) => {
      if (!trx || !trx.status) return 'status-default';
      const s = trx.status.toLowerCase();
      if (s.includes('confirmed')) return 'status-confirmed';
      if (s.includes('cancel'))   return 'status-cancelled';
      if (s.includes('active'))   return 'status-active';
      return 'status-default';
    };

    const getStatusLabel = (trx) => {
      if (!trx || !trx.status) return '-';
      const s = trx.status.toLowerCase();
      if (s.includes('confirmed')) return 'Confirmed';
      if (s.includes('cancel'))   return 'Cancelled';
      if (s.includes('active'))   return 'Active';
      return trx.status;
    };

    // handle format: "Cancelled [2026-06-24]" or plain "Cancelled"/"Canceled"
    const isCancelled = (trx) => {
      const s = trx.status?.toLowerCase() || '';
      return s.includes('cancel');
    };

    const getStatusTooltip = (trx) => {
      if (!trx || !trx.status) return '';
      const statusStr = trx.status;
      const bracketMatch = statusStr.match(/\[(.*?)\]|\((.*?)\)/);
      if (bracketMatch) {
        const dateStr = bracketMatch[1] || bracketMatch[2];
        return `Cancelled on ${dateStr}`;
      }
      const dateMatch = statusStr.match(/\d{4}-\d{2}-\d{2}/);
      if (dateMatch) {
        return `Cancelled on ${dateMatch[0]}`;
      }
      return statusStr;
    };

    const activeTransactionCount = computed(() => {
      if (!reportData.value?.transactions) return 0;
      return reportData.value.transactions.filter(trx => !isCancelled(trx)).length;
    });

    const activeTransactionAmount = computed(() => {
      if (!reportData.value?.transactions) return 0;
      return reportData.value.transactions
        .filter(trx => !isCancelled(trx))
        .reduce((sum, trx) => sum + (Number(trx.amount) || 0), 0);
    });

    const cancelledTransactionCount = computed(() => {
      if (!reportData.value?.transactions) return 0;
      return reportData.value.transactions.filter(trx => isCancelled(trx)).length;
    });

    const cancelledTransactionAmount = computed(() => {
      if (!reportData.value?.transactions) return 0;
      return reportData.value.transactions
        .filter(trx => isCancelled(trx))
        .reduce((sum, trx) => sum + (Number(trx.amount) || 0), 0);
    });

    return {
      loading,
      error,
      reportData,
      isPointSystemActive,
      reportTypeLabel,
      formatNumber,
      goBack,
      getSourceClass,
      getStatusClass,
      getStatusLabel,
      getStatusTooltip,
      isCancelled,
      activeTransactionCount,
      activeTransactionAmount,
      cancelledTransactionCount,
      cancelledTransactionAmount,
    };
  },
};
</script>

<style scoped>
/* Action Bar */
.detail-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
  padding: 0 4px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: #fff;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.btn-back:hover {
  background: #f9fafb;
  color: #111827;
  border-color: #9ca3af;
}

.report-date-badge {
  display: inline-flex;
  align-items: center;
  background: var(--primary-color);
  color: #ffffff;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Typography */
.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

/* Loading */
.detail-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

/* Summary Cards */
.summary-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.card-positive {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.card-positive .text-success {
  color: #155724 !important; /* Hijau tua */
}

.card-negative {
  background: #fef2f2;
  border-color: #fecaca;
}

.summary-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
}

.count-badge {
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-weight: 700;
}

.earn-badge {
  background: #dcfce7;
  color: #155724; /* Hijau tua */
}

.redeem-badge {
  background: #fee2e2;
  color: #991b1b;
}

.summary-value {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
}

/* Table Styling Improvements */
.transaction-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
}

.transaction-list-table th,
.transaction-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

/* Source and Point Base Badges */
.source-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
}

.source-badge.external-nett {
    background-color: #ebf5ff;
    color: #004e9a;
}

.source-badge.external-gross {
    background-color: #fff1f2;
    color: #9f1239;
}

.source-badge.internal {
    background-color: var(--secondary-color, #f0fdf4);
    color: var(--primary-color, #166534);
}

.point-value {
    font-weight: 700;
    font-size: 15px;
}

.text-theme {
    color: var(--primary-color) !important;
}

/* Status Summary Cards */
.card-status-active {
    background: #f0fdf4;
    border-color: #bbf7d0;
}

.card-status-cancelled {
    background: #fef2f2;
    border-color: #fecaca;
}

.status-count-note {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 0.25rem;
    display: block;
}

/* Transaction Status Badges */
.trx-status-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
    white-space: nowrap;
}

.trx-status-badge.status-active {
    background-color: #f0fdf4;
    color: #155724;
    border: 1px solid #bbf7d0;
}

.trx-status-badge.status-confirmed {
    background-color: #f0fdf4;
    color: #155724;
    border: 1px solid #bbf7d0;
}

.trx-status-badge.status-cancelled {
    background-color: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.trx-status-badge.status-default {
    background-color: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
}

/* Tooltip styling */
.status-badge-wrapper {
    position: relative;
    display: inline-block;
}

.status-badge-wrapper .custom-tooltip {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: 130%;
    right: 0;
    left: auto;
    transform: none;
    background-color: #1e293b;
    color: #ffffff;
    text-align: center;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: opacity 0.2s ease, visibility 0.2s ease;
    z-index: 1050;
    pointer-events: none;
}

.status-badge-wrapper .custom-tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 18px;
    left: auto;
    margin-left: 0;
    border-width: 5px;
    border-style: solid;
    border-color: #1e293b transparent transparent transparent;
}

.status-badge-wrapper:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
}
</style>
